import { useCallback, useRef } from 'react';

import { getSeoRedirectReq, getSeoResolveReq } from '@/features/seo-listing/api';
import { isPermalinkPath } from '@/features/seo-listing/consts';
import { canFollowSeoRedirectToUrl } from '@/features/seo-listing/utils/can-follow-seo-redirect';
import {
  applySeoResolveDocumentMeta,
  mergeListingFilters,
  mergeListingQueryOntoSeoToUrl,
  parseListingGetUrl,
  pushSeoRedirectHistory,
  reassertSeoRedirectHistory,
} from '@/features/seo-listing/utils';
import type { ParsedListingFilters, SeoResolveData } from '../types';

interface UseSeoRedirectOptions {
  // Called whenever filters were successfully restored from a resolved permalink
  onFiltersRestored: (filters: ParsedListingFilters) => void;
  // Called whenever SEO metadata (title/h1/canonical/...) was resolved,
  // used to patch React listing chrome without a full reload. Document head
  // tags are patched separately via applySeoResolveDocumentMeta.
  onMetaResolved: (meta: SeoResolveData) => void;
  // Called when SEO is disabled for this combination (404 from /seo/redirect
  // and no successful resolve) — caller should keep the regular GET-filter flow
  onSeoDisabled?: () => void;
  // Called exactly once at the very end of every non-aborted triggerSeoNavigation
  // run (success, soft-degraded resolve, not_found, or error) — the caller uses
  // this to release any guard flag it set before calling triggerSeoNavigation.
  // hasRedirected tells the caller whether window.history.pushState was actually
  // called (a real permalink redirect happened) so it can manually resync the
  // URL from current filter state when it wasn't (404/error — no pushState ran,
  // so the address bar was never touched and still needs the regular update).
  onNavigationSettled?: (hasRedirected: boolean) => void;
}

interface UseSeoRedirectResult {
  // Triggers the full redirect+resolve flow for a given fromUrl built from
  // current client filters. Safe to call repeatedly — in-flight requests are
  // aborted when a newer call starts.
  triggerSeoNavigation: (fromUrl: string) => Promise<void>;
  /** Abort in-flight redirect/resolve (e.g. before soft-reset to catalog). */
  abortSeoNavigation: () => void;
}

/**
 * Client-side counterpart of the middleware SEO redirect logic. Used when the
 * user changes a filter while already on /fanfics or a permalink page — i.e.
 * a live client-side interaction, not a fresh navigation handled by proxy.ts.
 *
 * Flow:
 * 1. Call /api/v1/seo/redirect with the current fromUrl.
 * 2. If it returns a redirect (200), pushState to toUrl and resolve metadata
 *    for the new permalink via /api/v1/seo/resolve (using pageKey when available).
 * 3. If it returns 404, SEO is disabled for this combination — the caller
 *    continues with the regular GET-filter fetch/fanfics-custom/v2 flow.
 *
 * The listing data fetch (fanfics-custom/v2) is NOT triggered here — it must
 * run in parallel, independently, from the calling component (e.g. fanfiction-grid's
 * existing loadFanfics()), so the listing never waits on this SEO round-trip.
 */
export const useSeoRedirect = ({
  onFiltersRestored,
  onMetaResolved,
  onSeoDisabled,
  onNavigationSettled,
}: UseSeoRedirectOptions): UseSeoRedirectResult => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const abortSeoNavigation = useCallback((): void => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const triggerSeoNavigation = useCallback(
    async (fromUrl: string) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const redirectResult = await getSeoRedirectReq(fromUrl, controller.signal);

        if (controller.signal.aborted) return;

        if (redirectResult.status === 'not_found') {
          onSeoDisabled?.();
          onNavigationSettled?.(false);
          return;
        }

        if (redirectResult.status === 'error') {
          // Fail-open: treat as SEO-disabled rather than blocking the user
          onSeoDisabled?.();
          onNavigationSettled?.(false);
          return;
        }

        const { toUrl: rawToUrl, pageKey } = redirectResult.data;
        // Keep pass-through sort (and similar) from fromUrl when the SEO
        // redirect response drops them — e.g. fandom?sort=popular → character
        // permalink without sort. Multi-character GET path keeps sort via
        // updateURL; single-character SEO path must merge explicitly.
        const toUrl = mergeListingQueryOntoSeoToUrl(fromUrl, rawToUrl);
        const toPath = toUrl.split('?')[0] ?? '';

        // Confirm the target is a live SEO page before leaving the GET URL.
        // Disabled SEO: redirect may still return a permalink while resolve is
        // 404 — following it soft-navigates onto a URL that hard-refreshes into
        // a GET ↔ permalink loop (tags). Match fandom/character: stay on GET.
        // Probe permalinks by path (same signal as SSR pages), not pageKey alone.
        const resolveProbe = await getSeoResolveReq(
          isPermalinkPath(toPath) ? { path: toPath } : pageKey ? { pageKey } : { path: toUrl },
          controller.signal,
        );

        if (controller.signal.aborted) return;

        if (
          resolveProbe.status !== 'ok' ||
          !canFollowSeoRedirectToUrl(toUrl, resolveProbe.status === 'ok')
        ) {
          onSeoDisabled?.();
          onNavigationSettled?.(false);
          return;
        }

        // Push the permalink without mutating the previous history entry.
        // Stripping query via replaceState(pathname) before push erased
        // pass-through URLs (e.g. /fandoms/…?characters=id) so Back skipped
        // them after a later character/fandom permalink navigation.
        // Leftover GET params from filter races are blocked by isSeoPending;
        // reassertSeoRedirectHistory below cleans the newly pushed entry.
        pushSeoRedirectHistory(toUrl);

        onMetaResolved(resolveProbe.data);
        // listingGetUrl represents the canonical GET-equivalent of THIS
        // permalink's own identity dimension(s) only (e.g. for a fandom
        // permalink, just `fandom=...`) — it does NOT include pass-through
        // filter dimensions that are not part of the permalink's identity
        // but were still preserved in toUrl's own query string (e.g.
        // ?tags=11 on a character permalink). Restoring filters from
        // listingGetUrl alone would therefore wipe out any such pass-through
        // selection the user just made. Merge both sources: prefer
        // listingGetUrl's value for a dimension when it provides one
        // (it is authoritative for the permalink's identity dimensions),
        // falling back to toUrl's own query string for everything else.
        const listingFilters = parseListingGetUrl(resolveProbe.data.listingGetUrl);
        const toUrlFilters = parseListingGetUrl(toUrl);
        const mergedFilters = mergeListingFilters(listingFilters, toUrlFilters);
        // onFiltersRestored is expected to synchronously flush its state
        // update (e.g. via flushSync) so that any URL-sync layout effect
        // reacting to it has already run — by the time onFiltersRestored
        // returns — using the still-true guard flag before the caller
        // clears it in onNavigationSettled below.
        onFiltersRestored(mergedFilters);
        applySeoResolveDocumentMeta(resolveProbe.data);
        // Re-assert the canonical toUrl as the final step — guarantees the
        // address bar stays clean even if an intervening render/effect
        // appended query params onto it during the async gap above.
        reassertSeoRedirectHistory(toUrl);
        onNavigationSettled?.(true);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        console.error('[useSeoRedirect] navigation failed:', err);
        onSeoDisabled?.();
        onNavigationSettled?.(false);
      }
    },
    [onFiltersRestored, onMetaResolved, onSeoDisabled, onNavigationSettled],
  );

  return { triggerSeoNavigation, abortSeoNavigation };
};
